import { Select } from 'antd'
import { observer } from 'mobx-react-lite'

import type { KeyboardEvent } from 'react'

import { LETTER_TEXTS as T } from '../Contracts/ILetter'
import { normalizeAddresses } from '../Helpers/letterAddresses'

import type { LetterRecipientOption } from '../Helpers/letterAddresses'
import type { FieldMethods } from '@/Core/UI/Form/types'

/** Звідки поле бере підказки: стор шухляди (`LetterDrawerStore.recipients`). */
export interface LetterRecipientsSource {
    recipients: LetterRecipientOption[]
    recipientsLoading: boolean
}

interface LetterAddressInputProps {
    field?: FieldMethods
    index?: number
}

/**
 * Поле адрес листа (`SPEC-013` §9.4): «E-mail адреси для відправки», копії,
 * приховані копії.
 *
 * Перелік адрес тегами (antd `Select` у режимі `tags`, а не ядровий
 * `TagsInput`: той підписаний «New Tag» і має вписаний білий фон, що ламає
 * темну тему). Підказки — пошти власника листа і його контактів; будь-яку
 * іншу адресу вписують руками, кома чи крапка з комою розділяють кілька
 * (так їх вставляють із пошти одним рядком). Значення одразу зводиться до
 * нижнього регістру й без повторів — як його зберігає бек.
 *
 * Фабрика, бо підказки свої в кожного листа: компонент читає їх зі стору
 * відкритої шухляди.
 */
export const createLetterAddressInput = (source: LetterRecipientsSource) =>
    observer(({ field, index }: LetterAddressInputProps) => {
        if (!field) return null

        const bound = field.bind({ index: index ?? 0 })
        const value = normalizeAddresses(bound.value)
        const fieldInputProps = (field as { inputProps?: Record<string, unknown> }).inputProps
        const disabled = bound.disabled === true || fieldInputProps?.['disabled'] === true

        // Tab у rc-select діє як Enter — «обирає» активний варіант. Після Enter
        // активним лишається щойно доданий тег, і Tab знімав його: адреса
        // зникала, щойно людина переходила до наступного поля. З порожнім
        // пошуком додавати нічого, тож Tab лише переводить фокус; набраний
        // текст Tab, як і раніше, перетворює на тег
        const onKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Tab' && event.target instanceof HTMLInputElement && event.target.value === '') {
                event.stopPropagation()
            }
        }

        return (
            <div onKeyDownCapture={onKeyDownCapture}>
                <Select
                    mode="tags"
                    style={{ width: '100%' }}
                    value={value}
                    options={source.recipients}
                    loading={source.recipientsLoading}
                    disabled={disabled}
                    placeholder={T.addressPlaceholder}
                    tokenSeparators={[',', ';', ' ']}
                    optionFilterProp="label"
                    notFoundContent={null}
                    onChange={(next: unknown) => bound.onChange(normalizeAddresses(next))}
                    // `bind()` ядра `onBlur` не повертає, хоч тип його й обіцяє: виклик
                    // без перевірки кидав TypeError на кожному виході з поля
                    onBlur={() => bound.onBlur?.()}
                />
            </div>
        )
    })
